import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, LoadingSkeleton, ToastContainer } from './ui';
import { PlusIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import { hasAnyRole } from '../utils/roles';

const feeTypes = ['tuition', 'sports', 'development', 'exam', 'uniform', 'transport', 'other'];
const CREATE_ROLES = ['administrator', 'principal'];

function Fees({ token, user }) {
  const [students, setStudents] = useState([]);
  const [fees, setFees] = useState([]);
  const [terms, setTerms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const canCreate = hasAnyRole(user, CREATE_ROLES);

  const addToast = (message, type = 'success') => setToasts(prev => [...prev, { message, type }]);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));
  const [formData, setFormData] = useState({
    student_id: '', term_id: '', amount: '', currency: 'USD', fee_type: 'tuition', due_date: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [studentsRes, feesRes, termsRes] = await Promise.all([
        fetch('/api/v1/students', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/fees', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/terms', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      setStudents(await studentsRes.json());
      setFees(await feesRes.json());
      setTerms(await termsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/v1/fees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setShowForm(false);
        fetchData();
        setFormData({ student_id: '', term_id: '', amount: '', currency: 'USD', fee_type: 'tuition', due_date: '' });
      } else {
        addToast(await safeErrorMessage(response, 'Failed to create fee invoice'), 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const getStatusBadge = (status, dueDate) => {
    if (status === 'paid') return <Badge variant="success">Paid</Badge>;
    if (status === 'partial') return <Badge variant="warning">Partial</Badge>;
    if (new Date(dueDate) < new Date() && status !== 'paid') return <Badge variant="danger">Overdue</Badge>;
    return <Badge variant="info">Pending</Badge>;
  };

  const columns = [
    { header: 'Student', accessor: 'student_name' },
    { header: 'Term', render: (row) => `Term ${row.term}` },
    { header: 'Amount', accessor: 'amount' },
    { header: 'Currency', accessor: 'currency' },
    { header: 'Fee Type', render: (row) => row.fee_type?.toUpperCase() },
    { header: 'Due Date', render: (row) => new Date(row.due_date).toLocaleDateString() },
    { header: 'Status', render: (row) => getStatusBadge(row.status, row.due_date) },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Fee Management</h2>
        {canCreate && (
          <Button variant="primary" size="sm" onClick={() => setShowForm(!showForm)}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            {showForm ? 'Cancel' : 'Create Invoice'}
          </Button>
        )}
      </div>

      {canCreate && showForm && (
        <Card>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Create Fee Invoice</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Student *</label>
                <select value={formData.student_id} onChange={(e) => setFormData({...formData, student_id: e.target.value})} required>
                  <option value="">Select Student</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.full_name} ({s.class_name})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Term *</label>
                <select value={formData.term_id} onChange={(e) => setFormData({...formData, term_id: e.target.value})} required>
                  <option value="">Select Term</option>
                  {terms.map(t => <option key={t.id} value={t.id}>Term {t.term_number} - {t.year}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Amount *</label>
                <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Currency *</label>
                <select value={formData.currency} onChange={(e) => setFormData({...formData, currency: e.target.value})}>
                  <option value="USD">USD</option>
                  <option value="ZWG">ZWG</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Fee Type *</label>
                <select value={formData.fee_type} onChange={(e) => setFormData({...formData, fee_type: e.target.value})}>
                  {feeTypes.map(type => <option key={type} value={type}>{type.toUpperCase()}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Due Date *</label>
                <input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} required />
              </div>
            </div>
            <Button type="submit" variant="primary">Create Invoice</Button>
          </form>
        </Card>
      )}

      <Card title="All Fee Invoices">
        {loading ? (
          <LoadingSkeleton type="text" rows={5} />
        ) : (
          <Table columns={columns} data={fees} emptyMessage="No fee invoices found." />
        )}
      </Card>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default Fees;
